import React from 'react'
import { ItemRenderProps } from '@components/UniversalAPISelector'
import { PermissionSet, useBusinessContext, useFlag, usePermission } from '@root/context'
import {
  DefaultQueryParams,
  IAccount,
  IAutoCompleteAccount,
  fetchAccount,
  fetchAccountSuggestions,
  fetchAutoCompleteAccounts
} from '@root/query'
import { motion } from 'framer-motion'
import { Button, InputProps, ModalProps, TextTooltip } from '@components'
import styled from 'styled-components'
import { FaBook, FaCheck, FaHistory, FaPen } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import UniversalAPISelector from '@root/components/UniversalAPISelector/UniversalAPISelector'
import { useScreen } from '@root/utils'
import { AccountForm } from '@pages/AccountsPage/components'
import { useQueryClient } from '@tanstack/react-query'

interface Props extends InputProps {
  onChange: (accountId: number) => void
  accountFilter?: DefaultQueryParams | null
  allowCreate?: boolean
}

const PAGE_SIZE = 100

type AccountSelectorItem = {
  type: 'suggestion' | 'account'
  item: IAutoCompleteAccount
  id: number
}

const filterSuggestions = (
  suggestions: IAutoCompleteAccount[],
  search: string
): IAutoCompleteAccount[] => {
  if (!search) return suggestions
  const normalizedSearch = search.toLowerCase()
  return suggestions.filter(suggestion => {
    const numberMatch = String(suggestion.number ?? '').startsWith(search)
    const nameMatch = suggestion.name?.toLowerCase().includes(normalizedSearch)
    return numberMatch || nameMatch
  })
}

const mergeAccountResults = (
  filteredSuggestions: IAutoCompleteAccount[],
  accounts: { results: IAutoCompleteAccount[] },
  page: number
): AccountSelectorItem[] => {
  const suggestionIds = new Set(filteredSuggestions.map(item => item.id))
  const filteredAccounts = accounts.results.filter(item => !suggestionIds.has(item.id))

  if (page !== 1) {
    return filteredAccounts.map(item => ({ type: 'account', item, id: item.id }))
  }

  return [
    ...filteredSuggestions.map(item => ({
      type: 'suggestion' as const,
      item,
      id: item.id
    })),
    ...filteredAccounts.map(item => ({ type: 'account' as const, item, id: item.id }))
  ]
}

const getSuggestions = (
  search: string,
  queryClient,
  suggestionsQueryKey: (string | number | Record<any, any>)[],
  fetchSuggestions: () => Promise<IAutoCompleteAccount[]>
): Promise<IAutoCompleteAccount[]> => {
  if (search) {
    return Promise.resolve(
      (queryClient.getQueryData(suggestionsQueryKey) as IAutoCompleteAccount[] | undefined) ?? []
    )
  }
  return queryClient.fetchQuery({
    queryKey: suggestionsQueryKey,
    queryFn: fetchSuggestions
  })
}

export const AccountSelector: React.FC<Props> = ({
  accountFilter: accountFilterProp,
  allowCreate,
  ...rest
}) => {
  const accountFilter = accountFilterProp ?? {}
  const { slug } = useBusinessContext()
  const { enabled: smartAccountSuggestionsEnabled } = useFlag('smart_account_suggestions')
  const { t } = useTranslation()
  const formId = 'account-form'
  const { isMobile } = useScreen()
  const hasAccountEditPermission = usePermission(PermissionSet.ACCOUNTS_EDIT)
  const queryClient = useQueryClient()

  const dataToShow = (item: AccountSelectorItem | IAutoCompleteAccount): string => {
    const account = 'item' in item ? item.item : item
    return `${account.number} ${account.name}`
  }

  const renderRow = (
    item: AccountSelectorItem,
    {
      isFocused,
      isSelected,
      onKeyDown,
      handleOnItemClick,
      handleOnEditClick,
      style,
      showEditItem
    }: ItemRenderProps<AccountSelectorItem>
  ) => {
    const account = item.item
    return (
      <ItemWrapper
        className={isFocused && 'keyboard-focused'}
        onKeyDown={onKeyDown}
        onClick={() => handleOnItemClick(item)}
        style={style}
        key={`${item.type}-${account.id}`}
      >
        <Item key={`account-${account.id}`} title={account.name}>
          <ItemColumn $isMobile={isMobile} className="id">
            {account.number}
          </ItemColumn>
          <ItemColumn $isMobile={isMobile}>{account.name}</ItemColumn>
          <RightItemsWrapper>
            {smartAccountSuggestionsEnabled && item.type === 'suggestion' && (
              <TextTooltip
                tooltip={t('uas.accounts.recentlyUsedInfo')}
                placement="top"
                zIndex={100001}
              >
                <SuggestionIcon>
                  <FaHistory />
                </SuggestionIcon>
              </TextTooltip>
            )}
            {showEditItem && hasAccountEditPermission && (
              <EditButtonWrapper>
                <Button
                  isSecondary={true}
                  icon={<FaPen />}
                  onClick={() => handleOnEditClick(item)}
                ></Button>
              </EditButtonWrapper>
            )}
            {isSelected && (
              <ItemColumn $isMobile={isMobile}>
                <CheckMarkWrapper>
                  <FaCheck />
                </CheckMarkWrapper>
              </ItemColumn>
            )}
          </RightItemsWrapper>
        </Item>
      </ItemWrapper>
    )
  }

  const createModalProps: Partial<ModalProps> = {
    width: 600,
    height: 800
  }

  const renderCreateForm = (onCreate: (data: IAccount) => void, item: IAccount) => {
    return <AccountForm account={item} onCreate={onCreate} />
  }

  return (
    <UniversalAPISelector
      {...rest}
      getItemQueryKeyFn={itemId => [slug, 'accounts', itemId]}
      getItem={async id => {
        const account = await fetchAccount({ slug, accountId: id })
        const autoCompleteAccount: IAutoCompleteAccount = {
          id: account.id,
          number: String(account.number),
          name: account.name
        }
        return { type: 'account', item: autoCompleteAccount, id: account.id }
      }}
      getItemsQueryKeyFn={params => [
        slug,
        'auto_complete_accounts',
        { ...params, page_size: PAGE_SIZE, ...accountFilter }
      ]}
      getItems={
        smartAccountSuggestionsEnabled
          ? async params => {
              const search = params?.search?.toString().trim() ?? ''
              const page = params?.page ?? 1
              const suggestionsQueryKey = [slug, 'account_suggestions', { ...accountFilter }]
              const [suggestions, accounts] = await Promise.all([
                getSuggestions(search, queryClient, suggestionsQueryKey, () =>
                  fetchAccountSuggestions({ slug }, { ...accountFilter })
                ),
                fetchAutoCompleteAccounts(
                  { slug },
                  { ...params, page_size: PAGE_SIZE, ...accountFilter }
                )
              ])
              const filteredSuggestions = filterSuggestions(suggestions, search)
              const results = mergeAccountResults(filteredSuggestions, accounts, page)

              return {
                ...accounts,
                results
              }
            }
          : async params => {
              const accounts = await fetchAutoCompleteAccounts(
                { slug },
                { ...params, page_size: PAGE_SIZE, ...accountFilter }
              )
              return {
                ...accounts,
                results: accounts.results.map(item => ({
                  type: 'account' as const,
                  item,
                  id: item.id
                }))
              }
            }
      }
      renderRow={renderRow}
      placeholder={t('uas.accounts.placeholder')}
      noDataMessage={t('uas.accounts.noData')}
      dropDownHeader={t('uas.accounts.allAccounts')}
      dropdownHeaderIcon={<FaBook />}
      formId={formId}
      getDataToShow={dataToShow}
      createModalProps={allowCreate && hasAccountEditPermission && createModalProps}
      renderCreateForm={allowCreate && hasAccountEditPermission && renderCreateForm}
    />
  )
}

const Item = styled(motion.div).attrs({
  variants: {
    hidden: {
      y: 5,
      opacity: 0
    },
    show: {
      y: 0,
      opacity: 1
    }
  }
})`
  display: flex;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;

  cursor: pointer;
`

const EditButtonWrapper = styled.div`
  display: none;
  align-self: center;
`

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &:hover ${EditButtonWrapper} {
    display: block;
  }
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const RightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const SuggestionIcon = styled.div`
  align-self: center;
  margin-left: ${({ theme }) => theme.spacing.xs}rem;

  svg {
    width: 0.85rem;
    height: 0.85rem;
    color: ${({ theme }) => theme.colors.iconGrayDark};
  }
`

const ItemColumn = styled.div<{ $isMobile: boolean }>`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.id {
    min-width: ${({ theme }) => theme.spacing.xxl}rem;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    color: ${({ theme }) => theme.colors.metalGray};
    font-variant-numeric: tabular-nums;
    font-family: 'Roboto Mono', monospace;

    ${({ theme, $isMobile }) =>
      $isMobile ? `margin-right:  0;` : `margin-right:  ${theme.spacing.sm}rem;`}
  }
`
const CheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;

  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`
